// src/pages/NewInvoicePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ItemSelect, QuickAddItemModal, NEW_ITEM_VALUE, itemLinkText, useSponsorLevels, LineSponsorLevel, itemTierId } from '../components/ItemPicker'; // [marker: item-list v1] [marker: fixes-oct21 v1]
import { AccountSelect } from '../components/AccountSelect'; // [marker: invoice-links v1]
import { ClassSelect } from '../components/ClassSelect'; // [marker: books-pro v1] class per line (blank = the event's, automatically)

const INVOICE_TYPES = [
  { value: 'general',      label: 'General' },
  { value: 'membership',   label: 'Membership' },
  { value: 'event_ticket', label: 'Event Ticket' },
  { value: 'sponsorship',  label: 'Sponsorship' },
];

const todayISO = () => new Date().toISOString().split('T')[0];
const plusDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

export default function NewInvoicePage() {
  const navigate = useNavigate();
  // [marker: invoice-links v1] ?businessId=&contactId=&type= when started from a company / contact page
  const qs = new URLSearchParams(useLocation().search);

  // Reference data
  const [businesses, setBusinesses] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [invoiceTypes, setInvoiceTypes] = useState(INVOICE_TYPES);
  const sponsorLevels = useSponsorLevels(); // [marker: fixes-oct21 v1]
  const [addingType, setAddingType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState('');

  // Form state
  const [form, setForm] = useState({
    businessId:    qs.get('businessId') || '',
    contactId:     qs.get('contactId') || '',
    recipientMode: 'business',   // 'business' | 'oneoff'
    recipientFirstName: '',
    recipientLastName:  '',
    recipientEmail:     '',
    invoiceType:   qs.get('type') || 'general',
    issueDate:     todayISO(),
    dueDate:       plusDaysISO(30),
    salespersonId: '',
    notes:         '',
    discountAmount: '0',
  });
  const [lineItems, setLineItems] = useState([
    { name: '', description: '', unitPrice: '', quantity: '1', catalogItemId: '', incomeAccountId: null },
  ]);
  const [saving, setSaving] = useState(false);
  const [addItemRow, setAddItemRow] = useState(null); /* [marker: item-list v1] row index for "+ Add new item" */

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Load reference data on mount
  useEffect(() => {
    Promise.all([
      api.get('/businesses?limit=5000'),
      api.get('/lookups/salespersons'),
      api.get('/invoice-items?is_active=all'), // [marker: invoice-links v1] any item, active or not
      api.get('/invoices/types'),
    ]).then(([bizRes, spRes, catRes, typesRes]) => {
      setBusinesses(bizRes.data?.data || bizRes.data || []);
      setSalespersons(spRes.data || []);
      setCatalogItems(catRes.data?.data || catRes.data || []);
      const types = (typesRes.data || []).map(t => ({ value: t.type_key, label: t.label }));
      if (types.length) setInvoiceTypes(types);
    }).catch(err => {
      console.error('Failed to load reference data:', err);
      toast.error('Failed to load form data.');
    }).finally(() => setLoadingRefData(false));
  }, []);

  // When business changes, load its contacts so the user can pick one
  useEffect(() => {
    if (!form.businessId) {
      setContacts([]);
      return;
    }
    api.get(`/businesses/${form.businessId}`)
      .then(res => setContacts(res.data?.contacts || []))
      .catch(() => setContacts([]));
  }, [form.businessId]);

  const addLineItem = () => {
    setLineItems(items => [...items, { name: '', description: '', unitPrice: '', quantity: '1', catalogItemId: '', incomeAccountId: null }]);
  };

  const removeLineItem = (index) => {
    setLineItems(items => items.length > 1 ? items.filter((_, i) => i !== index) : items);
  };

  const updateLineItem = (index, key, value) => {
    setLineItems(items => items.map((it, i) => i === index ? { ...it, [key]: value } : it));
  };

  const handleAddType = async () => {
    const label = newTypeLabel.trim();
    if (!label) return;
    try {
      const res = await api.post('/invoices/types', { label });
      const created = { value: res.data.type_key, label: res.data.label };
      setInvoiceTypes(prev => prev.some(t => t.value === created.value) ? prev : [...prev, created]);
      set('invoiceType', created.value);
      setNewTypeLabel('');
      setAddingType(false);
      toast.success(`Added invoice type "${created.label}".`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add type.');
    }
  };

  const applyCatalogItem = (index, catalogItemId, itemOverride) => {
    if (catalogItemId === NEW_ITEM_VALUE) { setAddItemRow(index); return; }
    if (!catalogItemId) {
      updateLineItem(index, 'catalogItemId', '');
      return;
    }
    const catItem = itemOverride || catalogItems.find(c => String(c.id) === String(catalogItemId));
    if (!catItem) return;
    setLineItems(items => items.map((it, i) => i === index ? {
      ...it,
      catalogItemId: catalogItemId,
      name:          catItem.name || it.name,
      description:   catItem.description || it.description,
      // [marker: sponsorship-products v1] products carry default_price (unit_price never existed)
      unitPrice:     (catItem.default_price ?? catItem.unit_price) != null ? String(catItem.default_price ?? catItem.unit_price) : it.unitPrice,
      incomeAccountId: catItem.income_account_id || it.incomeAccountId || null,
    } : it));
    // A sponsorship / ticket item switches a "general" invoice to the matching type
    if (String(catItem.category || '').toLowerCase() === 'sponsorship'
        && form.invoiceType === 'general'
        && invoiceTypes.some(t => t.value === 'sponsorship')) {
      set('invoiceType', 'sponsorship');
    } else if (catItem.source === 'ticket'
        && form.invoiceType === 'general'
        && invoiceTypes.some(t => t.value === 'event_ticket')) {
      set('invoiceType', 'event_ticket');
    }
  };

  // ── Totals ──
  const subtotal = lineItems.reduce((sum, it) => {
    const price = parseFloat(it.unitPrice) || 0;
    const qty   = parseFloat(it.quantity) || 0;
    return sum + price * qty;
  }, 0);
  const discount = parseFloat(form.discountAmount) || 0;
  const total    = Math.max(0, subtotal - discount);

  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

  // ── Save ──
  const validate = () => {
    if (form.recipientMode === 'business') {
      if (!form.businessId) return 'Please select a business, or switch to a one-off recipient.';
    } else {
      if (!form.recipientFirstName.trim() || !form.recipientLastName.trim() || !form.recipientEmail.trim()) {
        return 'For a one-off recipient, first name, last name, and email are required.';
      }
      if (!form.recipientEmail.includes('@') || !form.recipientEmail.includes('.')) {
        return 'Please enter a valid recipient email.';
      }
    }
    if (!form.dueDate) return 'Please pick a due date.';
    const hasAtLeastOne = lineItems.some(it => it.name.trim() && parseFloat(it.unitPrice) > 0);
    if (!hasAtLeastOne) return 'At least one line item with a name and price is required.';
    for (const [i, it] of lineItems.entries()) {
      if (it.name.trim() && (!it.unitPrice || isNaN(parseFloat(it.unitPrice)))) {
        return `Line item ${i + 1}: unit price must be a number.`;
      }
      if (it.name.trim() && (!it.quantity || isNaN(parseFloat(it.quantity)) || parseFloat(it.quantity) <= 0)) {
        return `Line item ${i + 1}: quantity must be greater than 0.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return toast.error(err);
    // [marker: fixes-oct21 v1] a sponsorship invoice should say which event it's for
    if (form.invoiceType === 'sponsorship') {
      const loose = lineItems.filter(it => it.name.trim() && !it.sponsorshipTierId && !itemTierId(catalogItems.find(c => String(c.id) === String(it.catalogItemId))));
      if (loose.length && !window.confirm(`${loose.map(l => `“${l.name.trim()}”`).join(', ')} ${loose.length === 1 ? 'isn’t' : 'aren’t'} linked to an event sponsorship level, so ${loose.length === 1 ? 'it' : 'they'} won’t show as a sponsor on the event or in the sponsor reports.\n\nSave anyway?`)) return;
    }

    // Strip out empty rows
    const cleanLineItems = lineItems
      .filter(it => it.name.trim())
      .map(it => ({
        catalogItemId: it.catalogItemId || null,
        name:          it.name.trim(),
        description:   it.description?.trim() || null,
        unitPrice:     parseFloat(it.unitPrice),
        quantity:      parseFloat(it.quantity) || 1,
        incomeAccountId: it.incomeAccountId || null,
        sponsorshipTierId: itemTierId(catalogItems.find(c => String(c.id) === String(it.catalogItemId))) ? null : (it.sponsorshipTierId || null), // [marker: fixes-oct21 v1]
        classId: it.classId || null,
      }));

    setSaving(true);
    const isOneOff = form.recipientMode === 'oneoff';
    try {
      const res = await api.post('/invoices', {
        businessId:     isOneOff ? null : parseInt(form.businessId),
        contactId:      (!isOneOff && form.contactId) ? parseInt(form.contactId) : null,
        recipientFirstName: isOneOff ? form.recipientFirstName.trim() : null,
        recipientLastName:  isOneOff ? form.recipientLastName.trim()  : null,
        recipientEmail:     isOneOff ? form.recipientEmail.trim()     : null,
        invoiceType:    form.invoiceType,
        issueDate:      form.issueDate,
        dueDate:        form.dueDate,
        salespersonId:  form.salespersonId ? parseInt(form.salespersonId) : null,
        notes:          form.notes || null,
        discountAmount: discount,
        lineItems:      cleanLineItems,
      });
      toast.success('Invoice created.');
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invoice.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRefData) {
    return <div className="loading-page"><div className="spinner" style={{ width: 32, height: 32 }} /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 4 }}>
            <Link to="/invoices" style={{ color: 'var(--gray-400)' }}>Invoices</Link> / New
          </div>
          <h1 className="page-title">New Invoice</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/invoices" className="btn btn-secondary">Cancel</Link>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving...</> : 'Save Draft'}
          </button>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 1240 }}>
        <div className="card-body">

          {/* Bill To: existing business or a one-off recipient */}
          <div className="form-group">
            <label className="form-label">Bill To</label>
            <div style={{ display: 'flex', gap: 20, padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 'var(--radius)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={form.recipientMode === 'business'}
                       onChange={() => set('recipientMode', 'business')} />
                Existing business / member
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={form.recipientMode === 'oneoff'}
                       onChange={() => set('recipientMode', 'oneoff')} />
                One-off recipient (non-member)
              </label>
            </div>
          </div>

          {form.recipientMode === 'business' ? (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Business *</label>
                <select className="form-select" value={form.businessId} onChange={e => { set('businessId', e.target.value); set('contactId', ''); }}>
                  <option value="">— Select a business —</option>
                  {businesses.map(b => (
                    <option key={b.id} value={b.id}>{b.business_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Contact (optional)</label>
                <select className="form-select" value={form.contactId} onChange={e => set('contactId', e.target.value)} disabled={!form.businessId || contacts.length === 0}>
                  <option value="">— No specific contact —</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.title ? ` (${c.title})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input className="form-input" value={form.recipientFirstName}
                         onChange={e => set('recipientFirstName', e.target.value)} placeholder="Jane" />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name *</label>
                  <input className="form-input" value={form.recipientLastName}
                         onChange={e => set('recipientLastName', e.target.value)} placeholder="Doe" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={form.recipientEmail}
                       onChange={e => set('recipientEmail', e.target.value)} placeholder="recipient@example.com" />
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 4 }}>
                  No business or contact record needed — just name and email.
                </div>
              </div>
            </>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Invoice Type</label>
              {!addingType ? (
                <select className="form-select" value={form.invoiceType}
                        onChange={e => {
                          if (e.target.value === '__add__') { setAddingType(true); return; }
                          set('invoiceType', e.target.value);
                        }}>
                  {invoiceTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  <option value="__add__">+ Add new type…</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" autoFocus placeholder="New type name"
                         value={newTypeLabel}
                         onChange={e => setNewTypeLabel(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddType(); } }} />
                  <button type="button" className="btn btn-primary" onClick={handleAddType}>Add</button>
                  <button type="button" className="btn btn-ghost"
                          onClick={() => { setAddingType(false); setNewTypeLabel(''); }}>Cancel</button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Salesperson (optional)</label>
              <select className="form-select" value={form.salespersonId} onChange={e => set('salespersonId', e.target.value)}>
                <option value="">— None —</option>
                {salespersons.map(s => (
                  <option key={s.id} value={s.id}>{s.full_name || s.initials}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Issue Date</label>
              <input className="form-input" type="date" value={form.issueDate} onChange={e => set('issueDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date *</label>
              <input className="form-input" type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes or message to display on invoice" />
          </div>

          {/* Line items */}
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Line Items</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addLineItem}>+ Add Line</button>
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Item</th>
                    <th style={{ minWidth: 180 }}>Name *</th>
                    <th style={{ minWidth: 150 }}>Description</th>
                    <th style={{ minWidth: 190 }}>Income Account</th>
                    <th style={{ width: 110 }}>Unit Price *</th>
                    <th style={{ width: 80 }}>Qty</th>
                    <th style={{ width: 100 }}>Line Total</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((it, i) => {
                    const lineTotal = (parseFloat(it.unitPrice) || 0) * (parseFloat(it.quantity) || 0);
                    return (
                      <tr key={i}>
                        <td>
                          <ItemSelect items={catalogItems} value={it.catalogItemId} onChange={v => applyCatalogItem(i, v)} />
                          {it.catalogItemId && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginTop: 3 }}>
                              {itemLinkText(catalogItems.find(c => String(c.id) === String(it.catalogItemId))) || 'Stand-alone item'}
                            </div>
                          )}
                          <LineSponsorLevel line={it} item={catalogItems.find(c => String(c.id) === String(it.catalogItemId))} invoiceType={form.invoiceType}
                                            tiers={sponsorLevels} onChange={v => updateLineItem(i, 'sponsorshipTierId', v)} />
                        </td>
                        <td>
                          <input className="form-input" type="text" value={it.name} onChange={e => updateLineItem(i, 'name', e.target.value)} placeholder="Item name" />
                        </td>
                        <td>
                          <input className="form-input" type="text" value={it.description} onChange={e => updateLineItem(i, 'description', e.target.value)} placeholder="Optional" />
                        </td>
                        <td>
                          <AccountSelect types={['income', 'other_income']} value={it.incomeAccountId}
                                         onChange={v => updateLineItem(i, 'incomeAccountId', v)} placeholder="— From item —" />
                          <ClassSelect value={it.classId} onChange={v => updateLineItem(i, 'classId', v)} placeholder="Class: automatic"
                                       style={{ marginTop: 4, height: 30, fontSize: '0.8rem', padding: '2px 6px' }} />
                        </td>
                        <td>
                          <input className="form-input" type="number" min="0" step="0.01" value={it.unitPrice} onChange={e => updateLineItem(i, 'unitPrice', e.target.value)} />
                        </td>
                        <td>
                          <input className="form-input" type="number" min="0" step="1" value={it.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)} />
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(lineTotal)}</td>
                        <td>
                          <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeLineItem(i)} disabled={lineItems.length === 1} title="Remove line">✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <div style={{ minWidth: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span>Subtotal</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span>Discount</span>
                <input className="form-input" type="number" min="0" step="0.01" value={form.discountAmount} onChange={e => set('discountAmount', e.target.value)} style={{ width: 110, textAlign: 'right' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--gray-200)', fontWeight: 700, fontSize: '1.1rem', marginTop: 4 }}>
                <span>Total</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 24, padding: 12, background: 'var(--gray-50)', borderRadius: 'var(--radius)', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            ℹ Invoice will be created in <strong>draft</strong> status. Use the invoice detail page to mark it as sent, void it, or record payments.
          </div>

        </div>
      </div>
      {addItemRow !== null && (
        <QuickAddItemModal
          initial={lineItems[addItemRow]}
          onClose={() => setAddItemRow(null)}
          onCreated={(item) => {
            setCatalogItems(list => [...list, item]);
            applyCatalogItem(addItemRow, String(item.id), item);
            setAddItemRow(null);
          }}
        />
      )}
    </div>
  );
}
